import { useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Sync from "@mui/icons-material/Sync";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { StatusChip } from "components/StatusChip";
import {
  JobStatus,
  JobTask,
  StaffJobsTableQuery,
  StaffJobsTableQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import { CAN_REQUEUE_JOBS_QUERY, STAFF_JOBS_TABLE_QUERY } from "./api";
import { JobDialog } from "./JobDialog";
import { RequeueJobConfirmDialog } from "./RequeueJobConfirmDialog";
import { canRequeue, getJobStatusChipColor } from "./utils";

type JobRow = StaffJobsTableQuery["jobs"]["items"][number];

export const Jobs = () => {
  useBreadcrumb({ label: "Jobs" });

  const { selectedId: jobId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("jobId");

  const { data: canRequeueData } = useQuery(CAN_REQUEUE_JOBS_QUERY);
  const canRequeueJobs = canRequeueData?.canRequeueJobs ?? false;

  const [requeueTarget, setRequeueTarget] = useState<{
    id: string;
    task?: JobTask | null;
    status: JobStatus;
  } | null>(null);

  const columns: OmnisearchGridColDef<JobRow>[] = useMemo(
    () => [
      {
        headerName: "Organization",
        field: "tenant",
        flex: 1.2,
        sortable: false,
        filterKeyType: "multipleTenant",
        valueGetter(_, row) {
          return row.tenant?.name ?? "";
        },
      },
      {
        headerName: "Job ID",
        field: "id",
        flex: 1,
      },
      {
        headerName: "Task",
        field: "task",
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(JobTask),
        flex: 1,
      },
      {
        headerName: "Status",
        field: "status",
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(JobStatus),
        flex: 0.7,
        renderCell({ value }: { value?: JobStatus }) {
          return <StatusChip label={value} color={getJobStatusChipColor(value)} />;
        },
      },
      {
        headerName: "Created At",
        field: "createdAt",
        flex: 1.2,
        filterKeyType: "time",
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        headerName: "Created By",
        field: "createdBy",
        flex: 1.2,
      },
      {
        headerName: "Updated At",
        field: "updatedAt",
        filterKeyType: "time",
        flex: 1.2,
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        type: "actions",
        field: "actions",
        getActions: ({ row }) => {
          const actions = [
            <IconButton
              key="open"
              component={Link}
              to={`./${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNew />
            </IconButton>,
          ];
          if (canRequeueJobs && canRequeue(row.status, row.task)) {
            actions.unshift(
              <Tooltip key="requeue" title="Re-queue this failed job (reset to queued and re-run)">
                <IconButton
                  aria-label="Re-queue job"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRequeueTarget({ id: row.id, task: row.task, status: row.status });
                  }}
                >
                  <Sync />
                </IconButton>
              </Tooltip>
            );
          }
          return actions;
        },
      },
    ],
    [canRequeueJobs]
  );

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<StaffJobsTableQuery, StaffJobsTableQueryVariables, JobRow>
        persistenceKey="staffJobs"
        isURLDriven
        initialFilters={[{ field: "createdAt", value: '"< 3 days"' }]}
        rowHeight="auto"
        initialSortModel={[{ field: "createdAt", sort: "desc" }]}
        onRowClick={(params) => {
          pushIdToUrl(params.row.id);
        }}
        columns={columns}
        dataQuery={STAFF_JOBS_TABLE_QUERY}
        getItems={(data) => data.jobs.items}
        getCount={(data) => data.jobs.count}
      />
      {!!jobId && (
        <JobDialog
          jobId={jobId}
          canRequeueJobs={canRequeueJobs}
          onClose={() => {
            popIdFromUrl();
          }}
        />
      )}
      {requeueTarget && (
        <RequeueJobConfirmDialog
          open
          jobId={requeueTarget.id}
          task={requeueTarget.task}
          status={requeueTarget.status}
          onClose={() => setRequeueTarget(null)}
        />
      )}
    </Stack>
  );
};
